import { useState, useEffect } from 'react';
import { IonApp, IonButton, isPlatform } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Storage } from '@capacitor/storage';
import { TextZoom } from '@capacitor/text-zoom';
import { Constants } from './defines/Constants';
import { CleanHTML, ShowSwal, GetMobileOperatingSystem } from './defines/Functions';
import Swal from 'sweetalert2';
import AppUrlListener from './components/AppUrlListener';
import axios from 'axios';
import Tabs from './components/Tabs';
import Header from './components/Header';
import Loading from './components/Loading';
import networkImg from './assets/images/no-internet.svg';
import exitImg from './assets/images/exit.svg';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
/* Theme variables */
import './theme/variables.css';
import './assets/fonts/iranyekan/css/style.css';

const App: React.FC = () => {
    useEffect(() => {
        TextZoom.set({ value: 1 });
    }, [])
    // ===================================     CONFIG     ====================================
    const configApi = Constants.api.config;
    const [config, setConfig] = useState(null) as any;
    useEffect(() => {
        const getConfig = async () => {
            try {
                const results = await axios.get(configApi);
                if (results.data.status === 'success') {
                    await Storage.set({
                        key: 'config',
                        value: JSON.stringify(results.data.config_items[0]),
                    });
                    setConfig(results.data.config_items[0]);
                }
            } catch (error: any) {
                console.log(error);
            }
        }
        getConfig();
    }, [configApi]);
    // ===================================    PWA Hint     ===================================
    const [pwaHint, setPwaHint] = useState(null) as any;
    useEffect(() => {
        if (config !== null) {
            if (!Capacitor.isNativePlatform() && !isPlatform('pwa')) {
                Storage.get({ key: 'pwaHint' }).then((response) => {
                    if (response.value === null) {
                        if (GetMobileOperatingSystem() === 'android' || GetMobileOperatingSystem() === 'windows-phone') {
                            setPwaHint(config.pwa_android_hint);
                        } else if (GetMobileOperatingSystem() === 'ios') {
                            setPwaHint(config.pwa_ios_hint);
                        } else {
                            setPwaHint(null);
                        }
                    } else {
                        setPwaHint(null);
                    }
                }).catch((err) => {
                    setPwaHint(null);
                    console.log(err);
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);
    const closePwaHint = async () => {
        await Storage.set({
            key: 'pwaHint',
            value: 'T',
        });
        setPwaHint(null);
    }
    // ===================================    EXIT APP    ====================================
    useEffect(() => {
        const swalOptions = {
            title: 'آیا از خروج از برنامه اطمینان دارید؟',
            iconHtml: '<img src="' + exitImg + '" width="100" />',
            customClass: {
                container: ''
            },
            backdrop: true,
            allowOutsideClick: false,
            showConfirmButton: true,
            confirmButtonText: 'بله',
            confirmButtonColor: '#333D79',
            showDenyButton: true,
            denyButtonText: 'خیر',
            denyButtonColor: '#F1C400',
            isConfirmed: () => CapacitorApp.exitApp(),
            isDenied: () => {
                Swal.close();
                logCurrentNetworkStatus();
            }
        }
        CapacitorApp.addListener("backButton", (e) => {
            if (window.location.pathname === "/") {
                ShowSwal(swalOptions);
            } else if (window.location.pathname === "/home") {
                ShowSwal(swalOptions);
            }
        });
    }, []);
    // ===================================    NETWORK     ====================================
    const [isConnect, setIsConnect] = useState(false);
    const logCurrentNetworkStatus = async () => {
        const swalOptionsNetwork = {
            text: 'خطا در برقراری ارتباط! لطفا از اتصال دستگاه خود به اینترنت اطمینان حاصل فرمایید.',
            iconHtml: '<img src="' + networkImg + '" width="100" />',
            customClass: {
                container: ''
            },
            backdrop: true,
            allowOutsideClick: false,
            showConfirmButton: false,
            confirmButtonText: 'تلاش مجدد',
            confirmButtonColor: '#333D79',
            showDenyButton: false,
            denyButtonText: 'خروج از برنامه',
            denyButtonColor: '#F1C400',
            isConfirmed: () => { },
            isDenied: () => CapacitorApp.exitApp()
        }
        const status = await Network.getStatus();
        if (status.connected) {
            setIsConnect(true);
            Swal.close();
        } else {
            setIsConnect(false);
            ShowSwal(swalOptionsNetwork);
        }
    };
    useEffect(() => {
        logCurrentNetworkStatus();
    }, []);
    Network.addListener('networkStatusChange', status => {
        logCurrentNetworkStatus();
    });
    // ===================================   COUNTRIES    ====================================
    const countriesApi = Constants.api.countries;
    useEffect(() => {
        const getCountries = async () => {
            try {
                const countriesResults = await axios.get(countriesApi);
                if (countriesResults.status === 200) {
                    await Storage.set({
                        key: 'countries',
                        value: JSON.stringify(countriesResults.data),
                    });
                }
            } catch (error: any) {
                console.log(error);
            }
        }
        getCountries();
    }, [countriesApi]);
    return (
        <IonApp>
            {isConnect &&
                <IonReactRouter>
                    {config === null &&
                        <Loading />
                    }
                    <AppUrlListener></AppUrlListener>
                    <Header phone={config !== null ? config.contact_phone : null} logo={config !== null ? config.site_logo : null} />
                    {pwaHint !== null ?
                        <div className="pwa-hint">
                            <div className="pwa-hint-box">
                                {CleanHTML(pwaHint)}
                                <IonButton color="warning" expand="block" onClick={closePwaHint}>
                                    متوجه شدم
                                </IonButton>
                            </div>
                        </div>
                        :
                        null
                    }
                    <Tabs />
                </IonReactRouter>
            }
        </IonApp>
    );
};
export default App;